// PrivateShowTicketShop.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import Loading from '../component/Loading.jsx';
import Rating from '../component/Rating.jsx';

import PrivateShowGift from '../container/PrivateShowGift.js';
import PrivateShowCircleProgressBar from '../container/PrivateShowCircleProgressBar.js';
import PrivateShowStatusTitle from '../container/PrivateShowStatusTitle.js';
import WithLivestreamRating from '../container/WithLivestreamRating.js';

import { color, textColor } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';

import {
  privateShowGiftType,
  privateShowStatus as privateShowStatusConstant,
} from '../resource/liveStreamConstants.js';
import { ModalId, ButtonId } from '../resource/mixpanel.js';
import { drmRulesetTestStatus } from '../resource/drmConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import {
  getCanUnlockByTicket,
  getIsWaitingProductData,
} from '../resource/livestreamUtils.js';

const giftTypes = [
  {
    type: privateShowGiftType.SINGLE,
    mixpanelButtonId: ButtonId.LiveStream.ButtonSingleTicket,
  },
  {
    type: privateShowGiftType.MORE,
    mixpanelButtonId: ButtonId.LiveStream.ButtonShowMore,
  },
  {
    type: privateShowGiftType.MVP,
    mixpanelButtonId: ButtonId.LiveStream.ButtonShowMvp,
  },
];

export class PrivateShowTicketShop extends React.PureComponent {
  state = {
    isBuyingTicket: false,
  };
  handleClickGift = ({ type }) => {
    const { updateOperationLivestream } = this.props;
    return updateOperationLivestream({
      selectedGiftType: type,
    });
  };
  handleSendTicket = () => {
    const {
      streamId,
      fundingId,
      giftId,
      selectedGiftType,
      moreCount,
      mvpCount,
      isUnlockModal,
      sendLiveStreamGift,
      privateShowStatus,
      playerStatus,
      streamStatus,
      streamTokenDecodedData,
    } = this.props;
    const modalId = isUnlockModal
      ? ModalId.LiveStream.LivestreamShowPurchaseModalPerforming
      : ModalId.LiveStream.LivestreamShowPurchaseModal;
    const privateShowData = {
      modalId,
      type: selectedGiftType,
      privateShowStatus,
      playerStatus,
      streamStatus,
      tokenNbf: streamTokenDecodedData?.nbf,
      tokenExp: streamTokenDecodedData?.exp,
      fundingId,
    };
    this.setState({
      isBuyingTicket: true,
    });
    switch (selectedGiftType) {
      case privateShowGiftType.MVP:
        return sendLiveStreamGift({
          streamId,
          giftId,
          modalId,
          count: mvpCount,
          privateShowData,
        });
      case privateShowGiftType.MORE:
        return sendLiveStreamGift({
          streamId,
          giftId,
          modalId,
          count: moreCount,
          privateShowData,
        });
      case privateShowGiftType.SINGLE:
        return sendLiveStreamGift({
          streamId,
          giftId,
          modalId,
          privateShowData,
        });
    }
    return;
  };
  getActionButtonContent = () => {
    const { privateShowStatus, isPrivateShowUnlocked, t } = this.props;
    const { isBuyingTicket } = this.state;
    if (this.getIsLoading()) {
      return <Loading />;
    }
    if (isBuyingTicket) {
      return t('ticket_purchase_successful', {
        ns: TranslationNamespace.LIVESTREAM,
      });
    }
    switch (privateShowStatus) {
      case privateShowStatusConstant.FUNDING:
        return t('ticket_button_raise', {
          ns: TranslationNamespace.LIVESTREAM,
        });
      case privateShowStatusConstant.FUNDING_FINAL_CALL:
        return t('ticket_button_last_call', {
          ns: TranslationNamespace.LIVESTREAM,
        });
      case privateShowStatusConstant.PREPARING:
      case privateShowStatusConstant.PERFORMING:
        if (isPrivateShowUnlocked) {
          return t('show_support_swagger', {
            ns: TranslationNamespace.LIVESTREAM,
          });
        }
        return t('show_join_this_show', {
          ns: TranslationNamespace.LIVESTREAM,
        });
      default:
        return <Loading />;
    }
  };
  getIsLoading = () => {
    const {
      giftSendingStatus,
      drmTestStatus,
      isPresenceGoalSubscribed,
      isPresenceStreamViewerSubscribed,
      isFetchingDrmLicense,
      isFetchingStreamToken,
      giftId,
      giftPrice,
    } = this.props;
    const isLoading =
      getIsWaitingProductData({
        productId: giftId,
        productPrice: giftPrice,
      }) ||
      drmRulesetTestStatus.TESTING === drmTestStatus ||
      giftSendingStatus === 'sending' ||
      !isPresenceGoalSubscribed ||
      !isPresenceStreamViewerSubscribed ||
      isFetchingDrmLicense ||
      isFetchingStreamToken;
    return isLoading;
  };
  componentDidUpdate(prevProps) {
    const { isSendGiftFetched, sendGiftFetchError } = this.props;
    if (
      isSendGiftFetched !== prevProps.isSendGiftFetched ||
      sendGiftFetchError !== prevProps.sendGiftFetchError
    ) {
      if (sendGiftFetchError != null && isSendGiftFetched) {
        this.setState({
          isBuyingTicket: false,
        });
      }
    }
  }
  render() {
    const {
      streamId,
      selectedGiftType,
      title,
      needAuthHandler,
      performDuration,
      t,
      isAuthed,
      login,
      addvalue,
      isUnlockModal,
      giftId,
      giftPrice,
      shouldShowCountdownWhenPerforming,
      mvpCount,
      moreCount,
    } = this.props;
    const { isBuyingTicket } = this.state;
    const performDurationInMinutes = Math.floor(performDuration / 60);
    const canUnlockByTicket = getCanUnlockByTicket({
      productId: giftId,
      productPrice: giftPrice,
    });
    const isLoading = this.getIsLoading();
    return (
      <StyledPrivateShowTicketShop>
        <WithLivestreamRating id={streamId}>
          {({
            ratingStepSize,
            displayRating,
            displayRatingCount,
            rating,
            isDisplayRatingEnabled,
          }) => {
            const shouldDisplayRating = !!rating && isDisplayRatingEnabled;
            return (
              <InformationWrapper shouldDisplayRating={shouldDisplayRating}>
                <ProgressWrapper>
                  <PrivateShowCircleProgressBar streamId={streamId} />
                </ProgressWrapper>
                {shouldDisplayRating && (
                  <RatingSummary>
                    <DisplayRating>{displayRating}</DisplayRating>
                    <Rating
                      value={displayRating || 0}
                      max={100 / ratingStepSize}
                      iconSize={20}
                      isReadOnly
                    />
                    <DisplayRatingCount>
                      {displayRatingCount}
                    </DisplayRatingCount>
                  </RatingSummary>
                )}
                <DurationWrapper isActive={shouldShowCountdownWhenPerforming}>
                  {shouldShowCountdownWhenPerforming ? (
                    <PrivateShowStatusTitle
                      streamId={streamId}
                      shouldShowCountdownWhenPerforming={
                        shouldShowCountdownWhenPerforming
                      }
                    />
                  ) : (
                    `${t('show_duration', {
                      ns: TranslationNamespace.LIVESTREAM,
                    })} ${t('minute_unit', {
                      ...[performDurationInMinutes],
                      ns: TranslationNamespace.LIVESTREAM,
                    })}`
                  )}
                </DurationWrapper>
                <TitleWrapper>{title}</TitleWrapper>
              </InformationWrapper>
            );
          }}
        </WithLivestreamRating>
        {(canUnlockByTicket || isLoading) && (
          <GiftListWrapper>
            {giftTypes.map(({ type, mixpanelButtonId }) => (
              <GiftItemWrapper
                key={type}
                data-element_id={mixpanelButtonId}
                data-tracking_payload={{
                  'gift.id': giftId,
                  'gift.price': giftPrice,
                  'show.ticket.mvp_count': mvpCount,
                  'show.ticket.goal_count': moreCount,
                }}
                onClick={() => this.handleClickGift({ type })}
              >
                <PrivateShowGift
                  streamId={streamId}
                  type={type}
                  isSelected={type === selectedGiftType}
                />
              </GiftItemWrapper>
            ))}
          </GiftListWrapper>
        )}
        {(canUnlockByTicket || isLoading || isBuyingTicket) && (
          <BodyActionButtonWrapper>
            {isAuthed ? (
              <BodyActionButton
                data-element_id={ButtonId.LiveStream.ButtonShowPurchaseModal}
                data-tracking_payload={{
                  modalId: isUnlockModal
                    ? ModalId.LiveStream.LivestreamShowPurchaseModalPerforming
                    : ModalId.LiveStream.LivestreamShowPurchaseModal,
                  'gift.id': giftId,
                  'gift.price': giftPrice,
                  'show.ticket.mvp_count': mvpCount,
                  'show.ticket.goal_count': moreCount,
                  'show.ticket.type_selected': selectedGiftType,
                }}
                onClick={this.handleSendTicket}
                disabled={isLoading || isBuyingTicket}
              >
                {this.getActionButtonContent()}
              </BodyActionButton>
            ) : (
              <BodyActionButton
                onClick={needAuthHandler === 'shop' ? addvalue : login}
              >
                {t('show_join_this_show', {
                  ns: TranslationNamespace.LIVESTREAM,
                })}
              </BodyActionButton>
            )}
          </BodyActionButtonWrapper>
        )}
      </StyledPrivateShowTicketShop>
    );
  }
}

PrivateShowTicketShop.propTypes = {
  streamId: PropTypes.string,
  selectedGiftType: PropTypes.string,
  fundingId: PropTypes.string,
  title: PropTypes.string,
  giftId: PropTypes.string,
  privateShowStatus: PropTypes.string,
  playerStatus: PropTypes.string,
  giftSendingStatus: PropTypes.string,
  drmTestStatus: PropTypes.string,
  needAuthHandler: PropTypes.string,
  moreCount: PropTypes.number,
  mvpCount: PropTypes.number,
  performDuration: PropTypes.number,
  giftPrice: PropTypes.number,
  shouldShowCountdownWhenPerforming: PropTypes.bool,
  isUnlockModal: PropTypes.bool,
  isPrivateShowUnlocked: PropTypes.bool,
  isPresenceGoalSubscribed: PropTypes.bool,
  isPresenceStreamViewerSubscribed: PropTypes.bool,
  isFetchingDrmLicense: PropTypes.bool,
  isFetchingStreamToken: PropTypes.bool,
  streamTokenDecodedData: PropTypes.object,
  sendLiveStreamGift: PropTypes.func,
  updateOperationLivestream: PropTypes.func,
  t: PropTypes.func.isRequired,
  streamStatus: PropTypes.string,
  isAuthed: PropTypes.bool,
  login: PropTypes.func,
  addvalue: PropTypes.func,
  isSendGiftFetched: PropTypes.bool,
  sendGiftFetchError: PropTypes.object,
};

PrivateShowTicketShop.defaultProps = {
  streamId: '',
  selectedGiftType: privateShowGiftType.MORE,
  fundingId: '',
  title: '',
  needAuthHandler: '',
  giftId: undefined,
  privateShowStatus: null,
  playerStatus: null,
  giftSendingStatus: null,
  drmTestStatus: null,
  moreCount: 1,
  mvpCount: 1,
  performDuration: 0,
  giftPrice: undefined,
  shouldShowCountdownWhenPerforming: false,
  isUnlockModal: false,
  isPrivateShowUnlocked: false,
  isPresenceGoalSubscribed: false,
  isPresenceStreamViewerSubscribed: false,
  isFetchingDrmLicense: false,
  isFetchingStreamToken: false,
  streamTokenDecodedData: null,
  sendLiveStreamGift: () => null,
  updateOperationLivestream: () => null,
  streamStatus: null,
  isAuthed: false,
  login: () => null,
  addvalue: () => null,
  isSendGiftFetched: false,
  sendGiftFetchError: null,
};

const StyledPrivateShowTicketShop = styled.div``;

const InformationWrapper = styled.div.attrs(({ shouldDisplayRating }) => ({
  style: {
    gridTemplateAreas: `${shouldDisplayRating ? "'progress rating-summary' " : ''}'progress title' 'progress duration'`,
  },
}))`
  margin-bottom: 12px;
  display: grid;
  grid-column-gap: 12px;
  grid-template-columns: 56px 1fr;
`;

const ProgressWrapper = styled.div`
  position: relative;
  grid-area: progress;
`;

const DurationWrapper = styled.div.attrs(({ isActive }) => ({
  style: {
    color: isActive ? color.lovense : color.grey['500'],
  },
}))`
  grid-area: duration;
  font-size: 12px;
  line-height: 1.5;
  text-align: left;
`;

const RatingSummary = styled.div`
  grid-area: rating-summary;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const DisplayRating = styled.div`
  margin-right: 4px;
  font-size: 18px;
  font-weight: 900;
  line-height: 1.5;
  color: ${color.neutral[100]};
`;

const DisplayRatingCount = styled.div`
  margin-left: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: ${color.neutral[75]};
  ::before {
    content: '(';
  }
  ::after {
    content: ')';
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  grid-area: title;
  font-size: 14px;
  line-height: 1.5;
  color: ${textColor.white};
`;

const GiftListWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GiftItemWrapper = styled.div`
  margin-right: 8px;
  flex: 1;
  max-width: 104px;
  overflow: hidden;
  :last-child {
    margin-right: 0px;
  }
`;

const BodyActionButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BodyActionButton = styled.button`
  ${ResetButtonStyle};
  border-radius: 4px;
  width: 220px;
  height: 36px;
  background-image: linear-gradient(to right, #fd5696 0%, #ffc100);
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: ${textColor.white};
`;

export default withTranslation()(PrivateShowTicketShop);
